import {
  Box,
  Center,
  Heading,
  IconButton,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {api} from 'api/setup';
import {IModalProps, Modal, UploadPhoto} from 'components/shared';
import React, {FC, useEffect, useState} from 'react';
import {modalMetroCloseButtonStyles, pinkScrollBarStyles} from 'theme';
import {IProduct} from 'types';
import ImageUI from 'ui/ImageUI';

import {useToast} from '../../../../hooks';

interface Props extends IModalProps {
  product: IProduct;
  onSuccess: () => void;
  onCloseParent: () => void;
}

export const MetroProductEditPhotoModal: FC<Props> = ({isOpen, onClose, product, onSuccess}) => {
  const {toast} = useToast();
  const [isLoading, setIsLoading] = useState({
    img: false,
    img_2: false,
    img_3: false,
  });

  const [photos, setPhotos] = useState({
    img: product.product_img_for_list_thumbnail || '',
    img_2: product.product_img_2_thumbnail || '',
    img_3: product.product_img_3_thumbnail || '',
  });

  useEffect(() => {
    setPhotos({
      img: product.product_img_for_list_thumbnail || '',
      img_2: product.product_img_2_thumbnail || '',
      img_3: product.product_img_3_thumbnail || '',
    });
  }, [product.product_img_for_list_thumbnail, product.product_img_2_thumbnail, product.product_img_3_thumbnail]);

  const handleUpload = async (file: File, field: 'img' | 'img_2' | 'img_3') => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Файл слишком большой',
        description: 'Максимальный размер файла: 5MB',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Неподдерживаемый формат файла',
        description: 'Разрешены только: JPEG, PNG, JPG',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading((prev) => ({...prev, [field]: true}));

      const formData = new FormData();
      formData.append(field, file);
      formData.append('id', String(product.id));

      const response = await api.post('/market-product/edit-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if ((response as any).updated_fields?.[field]) {
        toast({
          title: 'Фото успешно загружено',
          status: 'success',
          duration: 2000,
        });
        onSuccess();
      } else {
        throw new Error('Не удалось загрузить изображение');
      }
    } catch (error: any) {
      console.error('Ошибка загрузки изображения:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Неизвестная ошибка';
      toast({
        title: 'Ошибка загрузки фото',
        description: errorMessage,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading((prev) => ({...prev, [field]: false}));
    }
  };

  const renderPhotoBlock = (label: string, field: 'img' | 'img_2' | 'img_3') => (
    <Box key={field} mb={6} border="1px solid" borderColor="gray.200" borderRadius="lg" py={4} px={4}>
      <Center flexDirection="column">
        <Text mb={2} fontWeight="medium">
          {label}
        </Text>

        <Box position="relative" mb={3}>
          {photos[field] ? (
            <ImageUI src={photos[field]} alt={label} w="150px" h="150px" borderRadius="lg" objectFit="cover" />
          ) : (
            <Box
              w="150px"
              h="150px"
              border="1px dashed gray"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="sm" color="gray.400">
                No image
              </Text>
            </Box>
          )}

          {isLoading[field] && (
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="rgba(0,0,0,0.5)"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner color="white" size="lg" />
            </Box>
          )}
        </Box>

        <UploadPhoto
          isLoading={isLoading[field]}
          onImgChange={() => {}}
          onFileChange={(file) => handleUpload(file, field)}
        >
          <Text px={2}>{isLoading[field] ? 'Загрузка...' : 'Загрузить фото'}</Text>
        </UploadPhoto>
      </Center>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior>
      <ModalContent
        h={['100vh', '90vh']}
        overflow="hidden"
        borderRadius="3xl"
        maxW={['100%', '1000px']}
        display="flex"
        flexDirection="column"
      >
        <ModalCloseButton sx={modalMetroCloseButtonStyles} zIndex={10} />
        <ModalBody p={6} flex="1" overflowY="auto" sx={pinkScrollBarStyles}>
          <VStack align="stretch" spacing={6}>
            <Heading fontSize="2xl" mb={4}>
              Edit Product Photo: {product.name} ({product.article_number})
            </Heading>
            <Box display="flex" flexDirection={['column', 'row']} justifyContent="center" alignItems="center" gap={6}>
              {renderPhotoBlock('Photo 1 (Main)', 'img')}
              {renderPhotoBlock('Photo 2', 'img_2')}
              {renderPhotoBlock('Photo 3', 'img_3')}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface EditButtonProps {
  product: IProduct;
  onSuccess: () => void;
  onCloseParent: () => void;
}

export const ProductEditPhotoButton: FC<EditButtonProps> = ({product, onSuccess, onCloseParent}) => {
  const {isOpen: isOpenEditPhotoModal, onOpen: onOpenEditPhotoModal, onClose: onCloseEditPhotoModal} = useDisclosure();

  return (
    <>
      <IconButton
        aria-label="Edit product photo"
        icon={<ImageUI src="/edit-icon.svg" alt="Edit" />}
        size="sm"
        variant="solid"
        onClick={onOpenEditPhotoModal}
        borderRadius="full"
      />
      {isOpenEditPhotoModal && (
        <MetroProductEditPhotoModal
          isOpen={isOpenEditPhotoModal}
          onClose={onCloseEditPhotoModal}
          product={product}
          onSuccess={onSuccess}
          onCloseParent={onCloseParent}
        />
      )}
    </>
  );
};
